/**
 * Certificate Generator
 *
 * Handles the generation and download of accreditation certificates using pdf-lib.
 * This module fetches certificate data from the backend and populates a PDF template
 * with the retrieved information.
 */

import { PDFDocument, StandardFonts } from 'pdf-lib';
import download from 'downloadjs';

// PDF Template Paths - Edit these if template filenames change
const CERTIFICATE_TEMPLATE_PATH = '/dist/pdf/cert_template_simple.pdf';
const CERTIFICATE_TEMPLATE_GHOST_PATH = '/dist/pdf/cert_ghost_template_simple.pdf';

// Localized Script Keys
const TEMPLATE_DIR_KEY = 'templatedir';

/**
 * Generate and download an accreditation certificate for a facility.
 *
 * @param {number} facilityId - The facility entry ID
 * @param {string} restUrl - The REST API base URL
 * @param {string} restNonce - The WordPress REST API nonce for authentication
 * @param {string} templateUrl - The URL to the PDF template (optional, defaults to Ghost Template)
 * @returns {Promise<void>}
 */
export async function generateAccreditationCertificate(facilityId, restUrl, restNonce, templateUrl = null) {
    try {
        // Default to the certificate template if no template URL is provided
        if (!templateUrl) {
            templateUrl = localized[TEMPLATE_DIR_KEY] + CERTIFICATE_TEMPLATE_PATH;
        }

        // Fetch certificate data from the REST API
        const certificateData = await fetchCertificateData(facilityId, restUrl, restNonce);

        if (!certificateData.success) {
            throw new Error(certificateData.message || 'Failed to fetch certificate data');
        }

        const certMeta = certificateData.data;

        // Fetch the PDF template
        const formPdfBytes = await fetch(templateUrl).then(res => {
            if (!res.ok) {
                throw new Error('Failed to load PDF template');
            }
            return res.arrayBuffer();
        });

        // Load the PDF document
        const pdfDoc = await PDFDocument.load(formPdfBytes);

        // Get the form from the PDF
        const form = pdfDoc.getForm();

        // Populate the form fields with certificate data
        await populateFormFields(pdfDoc, form, certMeta);

        // Flatten the form to make fields non-editable
        form.flatten();

        // Save the PDF
        const pdfBytes = await pdfDoc.save();

        // Generate filename
        const filename = generateFilename(certMeta);

        // Download the PDF
        download(pdfBytes, filename, 'application/pdf');

    } catch (error) {
        console.error('Error generating certificate:', error);

        // Show error message to user
        if (typeof toastr !== 'undefined') {
            toastr.error(error.message || 'Failed to generate certificate', 'Error');
        } else {
            alert('Failed to generate certificate: ' + (error.message || 'Unknown error'));
        }

        throw error;
    }
}

/**
 * Fetch certificate data from the REST API.
 *
 * @param {number} facilityId - The facility entry ID
 * @param {string} restUrl - The REST API base URL
 * @param {string} restNonce - The WordPress REST API nonce
 * @returns {Promise<Object>} Certificate data
 */
async function fetchCertificateData(facilityId, restUrl, restNonce) {
    // Determine the correct endpoint based on the URL
    // Admin endpoint: /admin/certificate/{id}
    // Client endpoint: /application/certificate/{id}
    const endpoint = restUrl.includes('/admin')
        ? `${restUrl}/certificate/${facilityId}`
        : `${restUrl}/application/certificate/${facilityId}`;

    const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': restNonce
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
}

/**
 * Format the version number. If it's just a number like "1", format as "1.0".
 * Otherwise keep as is (e.g., "1.1", "1.2", etc.)
 *
 * @param {string} version - The version string (e.g., "Version 1", "1", "1.1")
 * @returns {string} Formatted version
 */
function formatVersion(version) {
    if (!version) {
        return '1.0';
    }

    // Extract numeric part from version string (handles "Version 1", "1", "1.1", etc.)
    const numericMatch = version.match(/(\d+(?:\.\d+)?)/);
    if (!numericMatch) {
        return '1.0';
    }

    const numericVersion = numericMatch[1];

    // If version is just a single digit without a decimal, add .0
    if (/^\d+$/.test(numericVersion)) {
        return `${numericVersion}.0`;
    }

    return numericVersion;
}

/**
 * Calculate appropriate font size based on text length.
 * Uses a simple character-based calculation as a reliable fallback.
 *
 * @param {string} text - The text to fit
 * @param {number} maxFontSize - Maximum font size to use (default 24)
 * @param {number} minFontSize - Minimum font size to use (default 13)
 * @returns {number} Calculated font size
 */
function calculateFontSizeByLength(text, maxFontSize = 24, minFontSize = 13) {
    const length = text.length;

    // Adjust these thresholds based on your field width
    if (length <= 30) return maxFontSize;      // 24pt
    if (length <= 38) return 20;
    if (length <= 45) return 18;
    if (length <= 53) return 16;
    if (length <= 62) return 15;
    if (length <= 72) return 14;

    return minFontSize;
}

/**
 * Populate PDF form fields with certificate data.
 *
 * @param {PDFDocument} pdfDoc - The PDF document object from pdf-lib
 * @param {PDFForm} form - The PDF form object from pdf-lib
 * @param {Object} certMeta - The certificate metadata
 */
async function populateFormFields(pdfDoc, form, certMeta) {
    // Embed italic font for the effective date field
    const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

    // Split the facility address into multiple lines
    const addressLines = (certMeta.facility_address || '').split('\n');

    // Format the version
    const formattedVersion = formatVersion(certMeta.application_version);

    // Construct the accreditation details statement (always starts with "This facility")
    const modality = certMeta.modality || 'MRI / CT';
    const accredDetails = `This facility is accredited for ${modality} under the RadSite Remote Scanning Accreditation Program, version ${formattedVersion}.`;

    // Construct the effective date range
    const startDate = certMeta.accreditation_start_date || '';
    const endDate = certMeta.accreditation_end_date || '';
    const effectiveDate = `Effective from ${startDate} through ${endDate}`;

    // Define field mappings based on actual PDF field names
    const fieldMappings = {
        'facility_name': certMeta.facility_name || '',
        'Address line 1': addressLines[0] || '',
        'Address line 2': addressLines[1] || '',
        'Address line 3': addressLines[2] || '',
        'accred_details': accredDetails,
        'effective': effectiveDate,
    };

    // Populate each field
    for (const [fieldName, value] of Object.entries(fieldMappings)) {
        try {
            const field = form.getTextField(fieldName);
            if (field) {
                const textValue = String(value);

                // Apply auto-sizing for facility name field BEFORE setting text
                if (fieldName === 'facility_name' && textValue.length > 0) {
                    const fontSize = calculateFontSizeByLength(textValue, 24, 10);
                    console.log(`Facility name: "${textValue}" (${textValue.length} chars) -> ${fontSize}pt`);
                    field.setFontSize(fontSize);
                }

                field.setText(textValue);

                // Apply italic font to the effective date field AFTER setting text
                if (fieldName === 'effective') {
                    field.defaultUpdateAppearances(italicFont);
                }
            }
        } catch (error) {
            // Field might not exist in template, or might be a different type
            console.warn(`Could not set field "${fieldName}":`, error.message);
        }
    }
}

/**
 * Generate a filename for the certificate PDF.
 *
 * @param {Object} certMeta - The certificate metadata
 * @returns {string} Generated filename
 */
function generateFilename(certMeta) {
    const companyName = (certMeta.company_name || 'Certificate')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();

    const facilityName = (certMeta.facility_name || 'Facility')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();

    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    return `accreditation_certificate_${companyName}_${facilityName}_${timestamp}.pdf`;
}
